/**
 * Dashboard — dezelfde blokken, endpoints en kolommen als de huidige bundle.
 *
 * Endpoints (uit de inventaris):
 *   /api/admin/dashboard/overview            KPI's, top spenders, tags
 *   /api/admin/models/platform-stats         volgers + betalende fans
 *   /api/admin/dashboard/model-performance   gesprekken per model
 *   /api/admin/dashboard/activity            live-activiteit
 *   /api/admin/dashboard/recent-subscriptions   betalende fans (tabel)
 */
import { useMemo, useState } from 'react'
import { Bar, Empty, ErrorNote, Kpi, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { ago, eur, num, pct } from '../lib/format'
import { useNavigate } from 'react-router-dom'
import { DonutChartPanel } from '../components/charts'
import { ModelNaam } from '../components/ModelNaam'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'

/**
 * De nette naam van een omzetsoort, zoals oud ze noemt.
 *
 * De server levert de sleutels zoals ze in de database staan
 * ("chat_attachment", "paid_message"); die zijn niet bedoeld om te lezen.
 */
function soortNaam(soort: string): string {
  const namen: Record<string, string> = {
    chat_attachment: 'Chat attachments',
    paid_message: 'Paid messages',
    subscription: 'Subscriptions',
    tip: 'Tips',
    video_unlock: 'Video unlocks',
    post: 'Posts',
    purchase: 'Purchases',
    refund: 'Refunds',
  }
  return namen[soort] ?? soort.replace(/_/g, ' ')
}

/** Tijdvenster-knoppen van de huidige bundle; de default is Today (days=1). */
const RANGES = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'Week' },
  { value: '30', label: 'Month' },
  { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' },
]
const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

interface Overview {
  conversations: { total: number; active: number; new: number }
  messages: { total: number; fan: number; ai: number; human: number }
  fans: { unique_active: number; new: number; vip: number; paying: number; top_spenders: TopSpender[] }
  ai_performance: { avg_confidence: number; avg_response_time_ms: number; takeover_count: number }
  tags: Record<string, number>
  revenue: {
    total: number; net_total: number; gross_total: number; events: number
    /** Waar de omzet vandaan komt, per soort — de bron van Earnings Breakdown. */
    by_type?: Record<string, { gross: number; net: number; count: number }>
  }
  models: { active: number }
}
interface TopSpender {
  fan_id: string; username: string; model: string
  gross_spend: number; net_spend: number
}
interface PaidSub {
  fan_id: number | null; fan_name: string; plan_months: number
  net_revenue: number; started_at: string | null; status: string
}
interface PerModel {
  model_slug: string; stage_name: string | null; platform: string
  paid_subs: PaidSub[] | null
  avatar_url: string | null
  // Velden die platform-stats al levert; nodig voor de kaarten per model (04-10-2026).
  follower_count: number; paid_fan_count: number; paid_revenue_eur: number | null
}
interface PlatformStats {
  totals: { followers: number; paid_fans: number; paid_revenue_eur: number }
  totals_by_platform: Record<string, { followers: number; paid_fans: number; paid_revenue_eur: number }>
  per_model: PerModel[]
}
interface ModelPerf {
  id: number; slug: string; stage_name: string; conversations: number
  fancentro_model_id: string | null; f2f_model_id: string | null
}
interface Activity {
  type: string; timestamp: string; model: string; platform: string; message: string
}

const ACT_KIND: Record<string, 'acc' | 'warn' | 'ok' | 'f2f' | 'mut'> = {
  post_published: 'acc', post_scheduled: 'warn', new_fan: 'f2f',
  ppv: 'ok', tip: 'ok', subscription: 'ok',
}

export default function Dashboard() {
  // Zelfde query-parameters als de huidige bundle: overview en
  // model-performance volgen het tijdvenster (default days=1 = Today), de
  // feeds hebben een vaste limit.
  const [days, setDays] = useState('1')
  const [platform, setPlatform] = useState('all')
  // 04-10-2026 (Jordy): het modelfilter stond alleen op Paying Fans; nu geldt
  // hij voor de hele pagina (kaarten, tabellen, grafieken), per pagina gekozen.
  const filter = useModelFilter()

  const navigate = useNavigate()
  const ov = useApi<Overview>(`/api/admin/dashboard/overview?days=${days}${filter.param}`)
  const stats = useApi<PlatformStats>('/api/admin/models/platform-stats')

  const perf = useApi<ModelPerf[]>(`/api/admin/dashboard/model-performance?days=${days}${filter.param}`)
  const act = useApi<Activity[]>('/api/admin/dashboard/activity?limit=40')
  // De tabel "Paying Fans" komt NIET uit recent-subscriptions maar uit
  // platform-stats: per_model[].paid_subs plat geslagen, gefilterd op de
  // platform-tab en het modelfilter, gesorteerd op started_at aflopend.
  // Precies zoals de huidige bundle het doet (16 rijen).

  // De bundle ververst de live-activiteit elke 8 seconden.
  usePoll(8000, act.refetch)

  const topPerf = useMemo(() => {
    // 07-10-2026 (Jordy): verborgen modellen niet in de tegels; alleen
    // Earnings toont ze samen als één regel.
    const rows = (perf.data ?? []).filter((m) => !(m as { hidden_group?: boolean }).hidden_group).filter((m) =>
      platform === 'all' ? true
      : platform === 'fancentro' ? !!m.fancentro_model_id
      : !!m.f2f_model_id,
    )
    return rows.slice().sort((a, b) => b.conversations - a.conversations).slice(0, 8)
  }, [perf.data, platform])

  const subRows = useMemo(() => {
    const rows = (stats.data?.per_model ?? []).flatMap((m) =>
      (m.paid_subs ?? []).map((s) => ({
        ...s,
        model: m.stage_name || m.model_slug,
        model_slug: m.model_slug,
        platform: m.platform,
      })),
    )
    return rows
      .filter((r) => platform === 'all' || (r.platform || '').toLowerCase() === platform)
      .filter((r) => filter.past(r.model_slug))
      .sort((a, b) => {
        const ta = a.started_at ? new Date(a.started_at).getTime() : 0
        const tb = b.started_at ? new Date(b.started_at).getTime() : 0
        return tb - ta
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats.data, platform, filter.waarde, filter.modellen])

  const actRows = useMemo(
    () => (act.data ?? []).filter((a) => (platform === 'all' || a.platform === platform) && filter.past(a.model)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [act.data, platform, filter.waarde, filter.modellen],
  )
  const maxConv = topPerf[0]?.conversations || 1
  const tags = ov.data?.tags ?? {}
  const maxTag = Math.max(1, ...Object.values(tags))

  /* Platformcijfers: zonder modelkeuze de totalen van de server, met keuze
     opgeteld uit per_model van alleen dat model. */
  const statSom = (plat?: string) => {
    if (filter.waarde === 'all') {
      const t = plat ? stats.data?.totals_by_platform?.[plat] : stats.data?.totals
      return { followers: t?.followers, paid_fans: t?.paid_fans,
               paid_revenue_eur: (t as { paid_revenue_eur?: number } | undefined)?.paid_revenue_eur }
    }
    const rr = (stats.data?.per_model ?? []).filter((r) => filter.past(r.model_slug) && (!plat || r.platform === plat))
    return {
      followers: rr.reduce((s, r) => s + (r.follower_count || 0), 0),
      paid_fans: rr.reduce((s, r) => s + (r.paid_fan_count || 0), 0),
      paid_revenue_eur: rr.reduce((s, r) => s + (r.paid_revenue_eur || 0), 0),
    }
  }
  const alle = statSom()
  const followersMeta = `F2F: ${num(statSom('f2f').followers)} · FC: ${num(statSom('fancentro').followers)}`

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <Seg options={RANGES} value={days} onChange={setDays} />
        <ModelFilter filter={filter} />
      </div>

      <div className="grid kpis">
        <Kpi icon="✉" label="Active Chats" value={num(ov.data?.conversations.active)}
             meta={`${num(ov.data?.conversations.new)} new`} />
        <Kpi icon="✎" label="Total Messages" value={num(ov.data?.messages.total)}
             meta={`AI: ${num(ov.data?.messages.ai)} · Fan: ${num(ov.data?.messages.fan)}`} />
        <Kpi icon="↗" label="Followers (platform)" value={num(alle.followers)}
             meta={followersMeta} />
        {/* Waarde = overview.fans.paying (volgt het tijdvenster); de
            ondertitel = platform-stats.totals — zoals de bundle het doet. */}
        <Kpi icon="€" label="Paying Fans" value={num(ov.data?.fans.paying)}
             meta={`${num(alle.paid_fans)} active · ${eur(alle.paid_revenue_eur)} mtd`} />
        <Kpi icon="✦" label="AI Confidence" value={pct((ov.data?.ai_performance.avg_confidence ?? 0) * 100)}
             meta={`${num(ov.data?.ai_performance.takeover_count)} takeovers`} />
        <Kpi icon="€" label="Revenue" value={eur(ov.data?.revenue.total)}
             meta={`Net: ${eur(ov.data?.revenue.net_total)} · ${num(ov.data?.revenue.events)} tx`} />
        <Kpi icon="⏱" label="Avg Response Time"
             value={`${((ov.data?.ai_performance.avg_response_time_ms ?? 0) / 1000).toFixed(1)}s`}
             meta="AI generation time" />
        <Kpi icon="◍" label="Active Models" value={num(ov.data?.models.active)} meta="Personas loaded" />
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.3fr 1fr' }}>
        <Panel title="Paying Fans"
               desc={`${num(alle.paid_fans)} active`}
               flush>
          {stats.loading ? <Loading /> : stats.error ? <ErrorNote error={stats.error} /> :
            !subRows.length ? <Empty>No active subscriptions</Empty> : (
            <Table cols={[
              { key: 'fan', label: 'Fan' }, { key: 'model', label: 'Model' },
              { key: 'plat', label: 'Platform' }, { key: 'plan', label: 'Plan' },
              { key: 'net', label: 'Net', num: true },
            ]}>
              {subRows.map((s, i) => (
                /* Oud opent vanuit deze rij het fandetail; gemeten met
                   scratchpad/rowdest.py -> /fans/show/<fan_id>. */
                <tr key={`${s.fan_id ?? s.fan_name}-${i}`}
                    className={s.fan_id ? 'clickrow' : undefined}
                    onClick={s.fan_id ? () => navigate(`/fans/show/${s.fan_id}`) : undefined}>
                  <td>{s.fan_name}</td>
                  <td><ModelNaam slug={s.model_slug} naam={s.model} /></td>
                  <td><Plat value={s.platform} /></td>
                  <td className="mut2">{s.plan_months}m</td>
                  <td className="num mono">{eur(s.net_revenue)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>

        {/* Oud heeft hier een paneel "Earnings Breakdown": waar de omzet in dit
            venster vandaan komt, per soort. Dat ontbrak volledig, waardoor je
            wel het totaal zag maar niet of het uit abonnementen, fooien of
            betaalde berichten kwam. */}
        <Panel title="Earnings Breakdown" desc="Where the revenue comes from">
          {ov.loading ? <Loading /> : ov.error ? <ErrorNote error={ov.error} /> : (
            <DonutChartPanel
              delen={Object.entries(ov.data?.revenue?.by_type ?? {})
                .filter(([, v]) => Number(v.net ?? 0) > 0)
                .sort((a, b) => Number(b[1].net ?? 0) - Number(a[1].net ?? 0))
                .map(([soort, v]) => ({
                  naam: soortNaam(soort),
                  waarde: Number(v.net ?? 0),
                }))}
              format={(v) => eur(v)}
              midden={eur(ov.data?.revenue?.net_total)}
            />
          )}
        </Panel>

        <Panel title="Top Spenders" flush>
          {ov.loading ? <Loading /> : ov.error ? <ErrorNote error={ov.error} /> : (
            <Table cols={[
              { key: 'fan', label: 'Fan' }, { key: 'model', label: 'Model' },
              { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
            ]}>
              {(ov.data?.fans.top_spenders ?? []).map((s, i) => (
                <tr key={`${s.fan_id}-${i}`}>
                  <td>{s.username}</td>
                  <td><ModelNaam naam={s.model} /></td>
                  <td className="num mono">{eur(s.gross_spend)}</td>
                  <td className="num mono mut2">{eur(s.net_spend)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.3fr 1fr' }}>
        <Panel title="Model Performance" desc="Conversations opened per model">
          {perf.loading ? <Loading /> : perf.error ? <ErrorNote error={perf.error} /> : (
            <div className="bars">
              {topPerf.map((m) => (
                <Bar key={m.id} label={<ModelNaam id={m.id} slug={m.slug} size={18} />} value={m.conversations} max={maxConv}
                     variant={m.fancentro_model_id ? 'fc' : 'f2f'} />
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Chat Tags" desc="Distribution of conversation tags">
          {ov.loading ? <Loading /> : Object.keys(tags).length === 0 ? (
            <Empty>Data populates as chats happen</Empty>
          ) : (
            <div className="bars">
              {Object.entries(tags).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <Bar key={k} label={k} value={v} max={maxTag} />
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Live Activity" desc="Auto-refreshes every 8s" flush
             right={<Tag kind="ok"><span className="dotst on-live" />Live</Tag>}>
        {act.loading && !act.data ? <Loading /> : act.error ? <ErrorNote error={act.error} /> :
          !actRows.length ? <Empty>No activity yet</Empty> : (
          <div className="feed">
            {actRows.slice(0, 20).map((a, i) => (
              <div className="it" key={`${a.timestamp}-${i}`}>
                <span className={`st tag ${ACT_KIND[a.type] || 'mut'}`}>
                  {a.type.replace(/_/g, ' ')}
                </span>
                <div>
                  <ModelNaam naam={a.model} size={18} vet /> <span className="mut2">· {a.platform}</span>
                  <div className="mut" style={{ marginTop: 2 }}>{a.message}</div>
                </div>
                <span className="tm">{ago(a.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  )
}
